import axios from 'axios';
import {
  BULK_ASSIGN_COMPLIANCE_QUESTIONS_URL,
  TOGGLE_TRANSITION_RULE_DOCUMENT_URL,
  get_request_config
} from '@/utils/system_constants/constant';

export default {
  get_document_rule_assignments(document) {
    const section_question_id = Number(document?.section_question_id || 0);
    if (!section_question_id) {
      return [];
    }

    return this.all_rule_documents
      .filter((assigned_document) => Number(assigned_document.section_question_id) === section_question_id)
      .filter((assigned_document, index, assignments) =>
        assignments.findIndex((item) => Number(item.rule_id) === Number(assigned_document.rule_id)) === index
      );
  },
  get_document_rule_label(document) {
    const assignments = this.get_document_rule_assignments(document);

    return assignments.length
      ? assignments.map((assignment) => assignment.rule_name).join(', ')
      : 'Not assigned';
  },
  get_selected_rule_checkbox_label(document) {
    if (!this.selected_rule) {
      return 'Select a rule first';
    }

    return this.is_document_assigned_to_selected_rule(document)
      ? `Linked to ${this.selected_rule.name}`
      : `Link to ${this.selected_rule.name}`;
  },
  get_selected_rule_checkbox_class(document) {
    if (!this.selected_rule) {
      return 'text-secondary';
    }

    return this.is_document_assigned_to_selected_rule(document)
      ? 'text-success'
      : 'text-secondary';
  },
  get_transition_rule_document_ids(transition_rule) {
    const rules = Array.isArray(transition_rule?.condition?.rules)
      ? transition_rule.condition.rules
      : [];
    const document_rule = rules.find((rule) => rule.action_type === 'document_requirement');

    return (document_rule?.selected_questions || [])
      .map((id) => String(id))
      .filter(Boolean);
  },
  is_document_assigned_to_selected_transition_rule(document) {
    const section_question_id = String(document?.section_question_id || '');
    return Boolean(section_question_id) && this.selected_transition_rule_document_ids.includes(section_question_id);
  },
  get_transition_rule_checkbox_label(document) {
    if (!this.selected_transition_rule) {
      return 'Select transition rule';
    }

    return this.is_document_assigned_to_selected_transition_rule(document)
      ? `Linked to ${this.selected_transition_rule.name}`
      : `Link to ${this.selected_transition_rule.name}`;
  },
  get_transition_rule_checkbox_class(document) {
    if (!this.selected_transition_rule) {
      return 'text-secondary';
    }

    return this.is_document_assigned_to_selected_transition_rule(document)
      ? 'text-success'
      : 'text-secondary';
  },
  get_transition_rule_type_label(transition_rule) {
    return transition_rule?.transition_type?.name || transition_rule?.transition_type_name || 'Transition';
  },
  get_transition_rule_status_label(transition_rule) {
    return transition_rule?.is_active === false ? 'Inactive' : 'Active';
  },
  get_transition_rule_condition_label(transition_rule) {
    const condition = transition_rule?.condition || {};
    const operator = condition.operator || 'AND';
    const rules = Array.isArray(condition.rules) ? condition.rules.length : 0;
    return `${operator} logic - ${rules} condition${rules === 1 ? '' : 's'}`;
  },
  async toggle_transition_rule_document(document) {
    if (!this.selected_transition_rule_id) {
      return;
    }

    const section_question_id = String(document?.section_question_id || '');
    if (!section_question_id) {
      return;
    }

    const is_selected = this.is_document_assigned_to_selected_transition_rule(document);
    this.selected_transition_rule_document_ids = is_selected
      ? this.selected_transition_rule_document_ids.filter((id) => id !== section_question_id)
      : [...this.selected_transition_rule_document_ids, section_question_id];
    this.updating_document_rule_id = `transition-${section_question_id}`;

    try {
      const response = await axios.post(
        TOGGLE_TRANSITION_RULE_DOCUMENT_URL,
        {
          transition_rule_id: Number(this.selected_transition_rule_id),
          section_question_id: Number(section_question_id),
          is_assigned: !is_selected
        },
        get_request_config()
      );
      const response_data = this.parse_response_data(response.data);
      const updated_rule = response_data.data ? this.normalize_transition_rule(response_data.data) : null;
      if (updated_rule) {
        this.transition_rules = this.transition_rules.map((rule) =>
          String(rule.id) === String(updated_rule.id) ? updated_rule : rule
        );
        this.selected_transition_rule_document_ids = this.get_transition_rule_document_ids(updated_rule);
      }
      this.show_success(
        is_selected
          ? 'Document unlinked from transition rule.'
          : 'Document linked to transition rule.'
      );
    } catch (error) {
      this.selected_transition_rule_document_ids = is_selected
        ? [...this.selected_transition_rule_document_ids, section_question_id]
        : this.selected_transition_rule_document_ids.filter((id) => id !== section_question_id);

      const response_data = this.parse_response_data(error?.response?.data || {});
      this.show_error(response_data.message || 'Failed to update the selected transition rule document.');
    } finally {
      this.updating_document_rule_id = '';
    }
  },
  get_assigned_rule_document(document) {
    const section_question_id = Number(document?.section_question_id || 0);
    if (!section_question_id) {
      return null;
    }

    return this.assigned_rule_documents.find(
      (assigned_document) => Number(assigned_document.section_question_id) === section_question_id
    ) || this.all_rule_documents.find(
      (assigned_document) =>
        Number(assigned_document.section_question_id) === section_question_id &&
        Number(assigned_document.rule_id) === Number(this.selected_rule_id || 0)
    ) || null;
  },
  format_rule_duration(value) {
    if (!value) {
      return 'Optional';
    }

    const text = String(value);
    const day_match = text.match(/^(\d+)\s/);
    if (day_match) {
      const days = Number(day_match[1]);
      return `${days} day${days === 1 ? '' : 's'}`;
    }

    return text;
  },
  get_rule_trigger_status_label(rule) {
    const statuses = rule?.trigger_statuses || rule?.trigger_status || rule?.trigger_status_ids || [];
    if (Array.isArray(statuses) && statuses.length) {
      return statuses
        .map((status) => status?.status || status?.name || status?.label || status)
        .join(', ');
    }

    return rule?.trigger_status_name || 'No trigger status';
  },
  async assign_selected_documents_to_rule() {
    if (!this.selected_rule_id) {
      this.show_error('Please select a compliance rule.');
      return;
    }

    const section_question_ids = this.selected_document_ids
      .map((id) => Number(id))
      .filter((id) => Number.isInteger(id) && id > 0);

    if (!section_question_ids.length) {
      this.show_error('Please select at least one document field.');
      return;
    }

    this.assigning_rule = true;

    try {
      const response = await axios.post(
        BULK_ASSIGN_COMPLIANCE_QUESTIONS_URL,
        {
          compliance_rule_id: Number(this.selected_rule_id),
          section_question_ids
        },
        get_request_config()
      );
      const response_data = this.parse_response_data(response.data);
      await this.fetch_documents_for_selected_rule();
      await this.fetch_documents_for_all_rules();
      this.show_success(response_data.message || `${response_data.processed_count || section_question_ids.length} document field(s) assigned to the rule.`);
    } catch (error) {
      const response_data = this.parse_response_data(error?.response?.data || {});
      this.show_error(response_data.message || 'Failed to assign document fields to the rule.');
    } finally {
      this.assigning_rule = false;
    }
  },
  set_page(page) {
    const next_page = Math.min(Math.max(1, page), this.total_pages);
    this.current_page = next_page;
  },
  preview_document(document) {
    this.selected_document = document;
  },
  upload_document() {
    this.show_info('Select a form to view document upload fields.');
  }
};
